import { Components } from 'src/components';
import { setComponentAttributes } from 'shared/storybook-utils';

export default {
  title: 'Components/Headline',
  component: 'a1-headline',
  tags: ['autodocs'],
  parameters: {
    docs: {
      description: {
        component: 'Content Component - Used for section headings.',
      },
    },
    layout: 'centered',
  },
  argTypes: {
    size: {
      // override due to the options and types being in a different order
      control: { type: 'select' },
      options: ['6xl', '5xl', '4xl', '3xl', '2xl', 'xl', 'l', 'm', 's'],
      table: {
        type: { summary: '"6xl" | "5xl" | "4xl" | "3xl" | "2xl" | "xl" | "l" | "m" | "s"' },
      },
    },
  },
};

const elementName = 'a1-headline';

const Template = (args: Components.A1Headline): HTMLElement => {
  const elComponent = document.createElement(elementName);

  Object.entries(args).forEach(([key, value]) => {
    setComponentAttributes({ element: elComponent, key, value });
  });

  return elComponent;
};

export const Default: Story<Components.A1Headline> = Template.bind({});
Default.args = {
  text: 'Headline with default values',
};

export const Inversed: Story<Components.A1Headline> = Template.bind({});
Inversed.args = {
  text: 'Headline with default values',
  inverse: true,
};
Inversed.parameters = {
  backgrounds: {
    default: 'Inverse',
  },
};

export const Size6XL: Story<Components.A1Headline> = Template.bind({});
Size6XL.storyName = 'Size 6XL';
Size6XL.args = {
  ...Default.args,
  size: '6xl',
  text: 'Default Headline with 6XL Size',
};

export const Size5XL: Story<Components.A1Headline> = Template.bind({});
Size5XL.storyName = 'Size 5XL';
Size5XL.args = {
  ...Default.args,
  size: '5xl',
  text: 'Default Headline with 5XL Size',
};

export const Size4XL: Story<Components.A1Headline> = Template.bind({});
Size4XL.storyName = 'Size 4XL';
Size4XL.args = {
  ...Default.args,
  size: '4xl',
  text: 'Default Headline with 4XL Size',
};

export const Size3XL: Story<Components.A1Headline> = Template.bind({});
Size3XL.storyName = 'Size 3XL';
Size3XL.args = {
  ...Default.args,
  size: '3xl',
  text: 'Default Headline with 3XL Size',
};

export const Size2XL: Story<Components.A1Headline> = Template.bind({});
Size2XL.storyName = 'Size 2XL';
Size2XL.args = {
  ...Default.args,
  size: '2xl',
  text: 'Default Headline with 2XL Size',
};

export const SizeXL: Story<Components.A1Headline> = Template.bind({});
SizeXL.args = {
  ...Default.args,
  size: 'xl',
  text: 'Default Headline with XL Size',
};

export const SizeL: Story<Components.A1Headline> = Template.bind({});
SizeL.args = {
  ...Default.args,
  size: 'l',
  text: 'Default Headline with L Size',
};

export const SizeM: Story<Components.A1Headline> = Template.bind({});
SizeM.args = {
  ...Default.args,
  size: 'm',
  text: 'Default Headline with M Size',
};

export const SizeS: Story<Components.A1Headline> = Template.bind({});
SizeS.args = {
  ...Default.args,
  size: 's',
  text: 'Default Headline with S Size',
};

export const Secondary: Story<Components.A1Headline> = Template.bind({});
Secondary.args = {
  ...Default.args,
  size: '2xl',
  type: 'secondary',
  text: 'Headline with Secondary variant',
};

export const SecondaryInverse: Story<Components.A1Headline> = Template.bind({});
SecondaryInverse.args = {
  ...Default.args,
  size: '2xl',
  type: 'secondary',
  text: 'Inverted Headline with Secondary variant',
  inverse: true,
};
SecondaryInverse.parameters = {
  backgrounds: {
    default: 'Inverse',
  },
};

export const AccentTextUsingBoldTag: Story<Components.A1Headline> = Template.bind({});
AccentTextUsingBoldTag.args = {
  ...Default.args,
  size: '2xl',
  text: 'Headline with <b>accent / bold tag</b>',
  accent: 'purple',
};

export const AccentTextUsingEmTag: Story<Components.A1Headline> = Template.bind({});
AccentTextUsingEmTag.args = {
  ...Default.args,
  size: '2xl',
  text: 'Headline with <em>accent / em tag</em>',
  accent: 'purple',
};

export const AccentTextUsingTemplateSelector: Story<Components.A1Headline> = Template.bind({});
AccentTextUsingTemplateSelector.args = {
  ...Default.args,
  size: '2xl',
  text: 'Headline with {accent}accent / template{/accent}',
  accent: 'purple',
};

export const InverseAccent: Story<Components.A1Headline> = Template.bind({});
InverseAccent.args = {
  ...Default.args,
  size: '2xl',
  text: 'Inverted headline with <b>bold accent</b>',
  accent: 'purple',
  inverse: true,
};
InverseAccent.parameters = {
  backgrounds: {
    default: 'Inverse',
  },
};

export const YellowAccent: Story<Components.A1Headline> = Template.bind({});
YellowAccent.args = {
  ...Default.args,
  size: '2xl',
  text: 'Headline with <b>yellow accent</b>',
  accent: 'yellow',
};

export const OrangeAccent: Story<Components.A1Headline> = Template.bind({});
OrangeAccent.args = {
  ...Default.args,
  size: '2xl',
  text: 'Headline with <b>orange accent</b>',
  accent: 'orange',
};

export const RedAccent: Story<Components.A1Headline> = Template.bind({});
RedAccent.args = {
  ...Default.args,
  size: '2xl',
  text: 'Headline with <b>red accent</b>',
  accent: 'red',
};

export const PurpleAccent: Story<Components.A1Headline> = Template.bind({});
PurpleAccent.args = {
  ...Default.args,
  size: '2xl',
  text: 'Headline with <b>purple accent</b>',
  accent: 'purple',
};

export const AquaAccent: Story<Components.A1Headline> = Template.bind({});
AquaAccent.args = {
  ...Default.args,
  size: '2xl',
  text: 'Headline with <b>aqua accent</b>',
  accent: 'aqua',
};

export const BlueAccent: Story<Components.A1Headline> = Template.bind({});
BlueAccent.args = {
  ...Default.args,
  size: '2xl',
  text: 'Headline with <b>blue accent</b>',
  accent: 'blue',
};

export const TealAccent: Story<Components.A1Headline> = Template.bind({});
TealAccent.args = {
  ...Default.args,
  size: '2xl',
  text: 'Headline with <b>teal accent</b>',
  accent: 'teal',
};

export const GreenAccent: Story<Components.A1Headline> = Template.bind({});
GreenAccent.args = {
  ...Default.args,
  size: '2xl',
  text: 'Headline with <b>green accent</b>',
  accent: 'green',
};

export const Div: Story<Components.A1Headline> = Template.bind({});
Div.args = {
  ...Default.args,
  size: '2xl',
  htmlTag: 'div',
  text: 'Headline with div tag',
};

export const P: Story<Components.A1Headline> = Template.bind({});
P.args = {
  ...Default.args,
  size: '2xl',
  htmlTag: 'p',
  text: 'Headline with p tag',
};

export const Span: Story<Components.A1Headline> = Template.bind({});
Span.args = {
  ...Default.args,
  size: '2xl',
  htmlTag: 'span',
  text: 'Headline with span tag',
};

export const MultipleConfigurations: Story<Components.A1Headline> = Template.bind({});
MultipleConfigurations.args = {
  ...Default.args,
  size: 'm',
  htmlTag: 'span',
  headingLevel: 5,
  text: 'Headline with span tag, aria-level 5 and M size',
};
